/* eslint-disable @next/next/no-img-element */

'use client';

import { FC, ReactNode, useEffect, useMemo } from 'react';
import NextImage from 'next/image';
import Link from 'next/link';
import { Button, Flex, Text } from '@mantine/core';
import { IconCaretLeft, IconCaretRight, IconClockHour4, IconMessage, IconRadio } from '@tabler/icons-react';
import { useDebouncedValue, useLocalStorage } from '@mantine/hooks';
import { User } from 'lucia';
import { Blur } from '@/components/Blur/Blur';
import classes from './vods.module.css';
import { secondsToTime } from '@/lib/utils';
import { PlayingUser, setRadioVOD } from './actions';
import { RestrictedVOD, VOD } from '@/lib/vodTypes';

interface VODListProps {
    user: User | null;
    vods: VOD[];
    restrictedVods: (RestrictedVOD & { requiredLevel: string })[];
    playingUsers: PlayingUser[];
}

const PAGE_SIZE = 18;

const vodMatcher = (searchTerm: string, vod: VOD | (RestrictedVOD & { requiredLevel: string })) => {
    const onlyComments = searchTerm.includes('has:comments');
    const onlyNoComments = searchTerm.includes('has:no-comments');
    const term = searchTerm.replace('has:comments', '').replace('has:no-comments', '').trim();
    const commentCount = vod.commentCount ?? vod.comments.length;

    const title = vod.title ?? '';
    const streamed_at = vod.streamed_at ?? new Date();

    if (searchTerm.includes("song:")) {
        const idx = searchTerm.indexOf("song:") + 5;
        const songTerm = searchTerm.slice(idx).trim().toLocaleLowerCase();
        
        return (vod.songs ?? []).some(song => `${song.artist} - ${song.title}`.toLocaleLowerCase().includes(songTerm));
    }

    return (
        (title.toLowerCase().includes(term) || streamed_at.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }).toLowerCase().includes(term))
        && (onlyComments ? commentCount > 0 : true)
        && (onlyNoComments ? commentCount === 0 : true)
    );
};

export const VODList: FC<VODListProps> = ({ user, vods, restrictedVods, playingUsers }) => {
    const [currentPage, setCurrentPage] = useLocalStorage<number>({ key: 'vod-page', defaultValue: 1 });
    const [searchTerm, setSearchTerm] = useLocalStorage<string>({ key: 'vod-search', defaultValue: '' });

    const sanitizedSearchTerm = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();

        return term.length >= 3 ? term : '';
    }, [searchTerm]);

    const [debouncedSearchTerm] = useDebouncedValue(sanitizedSearchTerm, 500);

    const filteredVods = useMemo(() => vods.filter(vod => debouncedSearchTerm.trim() !== '' ? vodMatcher(debouncedSearchTerm, vod) : true), [vods, debouncedSearchTerm]);
    const filteredRestrictedVods = useMemo(() => restrictedVods.filter(vod => debouncedSearchTerm.trim() !== '' ? vodMatcher(debouncedSearchTerm, vod) : true), [restrictedVods, debouncedSearchTerm]);

    const pageCount = Math.ceil((filteredVods.length + filteredRestrictedVods.length) / PAGE_SIZE);

    const pageVods = useMemo(() => {
        const combined = [...filteredVods, ...filteredRestrictedVods];
        return combined.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    }, [filteredVods, filteredRestrictedVods, currentPage]);

    useEffect(() => {
        pageVods.forEach((v) => {
            const img = new Image();
            img.src = v.web_thumbnail_path;
        });
    }, [pageVods]);

    useEffect(() => {
        if ((pageCount > 0 && currentPage < 1) || currentPage > pageCount) {
            setCurrentPage(1);
        }
    }, [pageCount, currentPage]);

    const vodElements = useMemo(() => {
        const retVal = [];

        retVal.push(
            ...filteredVods.map((vod) => {
                const vodPlaying = playingUsers.filter(pu => pu.vodId === vod.id);

                const playing: ReactNode[] = [];
                vodPlaying.forEach((pu) => {
                    playing.push(
                        <NextImage
                          key={`${pu.vodId}-${pu.displayName}`}
                          src={pu.profileImageUrl}
                          alt={pu.displayName}
                          title={pu.displayName}
                          width={40}
                          height={40}
                          style={{ borderRadius: '50%' }}
                        />
                    );
                });

                return (
                    <Flex data-channel={vod.channel.name} key={`${vod.streamed_at.getTime()}-${vod.duration}-${vod.title}-${vod.id}`} className={classes.vod} direction="column" justify="stretch" align="center" px={10} py={5}>
                        <div className={classes.vodPlaying}>
                            {playing}
                        </div>
                        <p style={{ flexGrow: 0 }}>{vod.streamed_at.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                        <Link href={`/vods/${vod.id}`} style={{ flexGrow: 1 }}>
                            <img
                              width={320}
                              height={180}
                              src={vod.web_thumbnail_path}
                              alt={vod.title}
                              style={{ flexGrow: 0, borderRadius: '5px' }}
                            />
                            <h4>{vod.title}</h4>
                        </Link>
                        <Flex direction="row" align="center" justify="space-between" w="100%">
                            <Flex direction="row">
                                <IconClockHour4 /><Text>{secondsToTime(vod.duration)}</Text>
                            </Flex>
                            <Flex direction="row" gap={5}>
                                <IconMessage /><Text>{vod.commentCount ?? vod.comments.length}</Text>
                                { user && user.roles.admin && (
                                    <Link href="#" onClick={() => setRadioVOD(vod)}>
                                        <IconRadio />
                                    </Link>
                                )}
                            </Flex>
                        </Flex>
                    </Flex>
                );
            })
        );

        retVal.push(
            ...filteredRestrictedVods.map((vod, idx) => {
                    const { requiredLevel } = vod;

                    return (
                        <Flex className={`${classes.vod} ${classes.restricted}`} data-channel={vod.channel.name} key={`${vod.streamed_at.getTime()}-${vod.duration}-${vod.title}-${idx}`} direction="column" justify="stretch" align="center" p={5}>
                            <Blur>
                                <p style={{ flexGrow: 0 }}>{vod.streamed_at.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                                <img
                                  width={320}
                                  height={180}
                                  src={vod.web_thumbnail_path}
                                  alt={vod.title}
                                  style={{ flexGrow: 0, borderRadius: '5px' }}
                                />
                                <h4 style={{ flexGrow: 1 }}>{vod.title}</h4>
                            </Blur>
                            <Text className={classes.requiredLevel}>{requiredLevel}</Text>
                        </Flex>
                    );
                })
        );

        return retVal.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    }, [filteredVods, filteredRestrictedVods, playingUsers, currentPage, searchTerm]);

    return (
        <>
            <Flex direction="row" align="center" justify="center" mx={50} mt={25}>
                { currentPage > 1 ? (
                    <>
                        <Button variant="outline" color="white" onClick={() => setCurrentPage(1)} style={{ width: '75px' }}><IconCaretLeft /><IconCaretLeft /></Button>
                        <Button variant="outline" color="white" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} style={{ width: '75px' }}><IconCaretLeft /></Button>
                    </>
                ) : <div style={{ width: '75px', userSelect: 'none' }}>&nbsp;</div>}
                { pageCount > 0 ? (
                    <Text w={130} mx={20}>Seite {currentPage} von {pageCount}</Text>
                ) : (
                    <Text w={200} mx={20}>Keine VODs gefunden</Text>
                )}
                { currentPage < pageCount ? (
                    <>
                        <Button disabled={currentPage >= pageCount} variant="outline" color="white" onClick={() => setCurrentPage(p => Math.min(pageCount, p + 1))} style={{ width: '75px' }}><IconCaretRight /></Button>
                        <Button disabled={currentPage >= pageCount} variant="outline" color="white" onClick={() => setCurrentPage(pageCount)} style={{ width: '75px' }}><IconCaretRight /><IconCaretRight /></Button>
                    </>
                ) : <div style={{ width: '75px', userSelect: 'none' }}>&nbsp;</div>}
            </Flex>
            <Flex direction="row" align="center" justify="center" mx={50} mt={10}>
                <input className={classes.search} type="text" placeholder="VODs durchsuchen..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
            </Flex>
            <Flex direction="row" align="stretch" justify="center" gap={10} rowGap={25} wrap="wrap" my={50}>
                {vodElements}
            </Flex>
            <Flex direction="row" align="center" justify="center" mx={50} mt={25}>
                { currentPage > 1 ? (
                    <>
                        <Button variant="outline" color="white" onClick={() => setCurrentPage(1)} style={{ width: '75px' }}><IconCaretLeft /><IconCaretLeft /></Button>
                        <Button variant="outline" color="white" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} style={{ width: '75px' }}><IconCaretLeft /></Button>
                    </>
                ) : <div style={{ width: '75px', userSelect: 'none' }}>&nbsp;</div>}
                { pageCount > 0 ? (
                    <Text w={130} mx={20}>Seite {currentPage} von {pageCount}</Text>
                ) : (
                    <Text w={200} mx={20}>Keine VODs gefunden</Text>
                )}
                { currentPage < pageCount ? (
                    <>
                        <Button disabled={currentPage >= pageCount} variant="outline" color="white" onClick={() => setCurrentPage(p => Math.min(pageCount, p + 1))} style={{ width: '75px' }}><IconCaretRight /></Button>
                        <Button disabled={currentPage >= pageCount} variant="outline" color="white" onClick={() => setCurrentPage(pageCount)} style={{ width: '75px' }}><IconCaretRight /><IconCaretRight /></Button>
                    </>
                ) : <div style={{ width: '75px', userSelect: 'none' }}>&nbsp;</div>}
            </Flex>
        </>
    );
};
